import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Wordmark from "@/components/Wordmark";

const LINKS = [
  { label: "Home", to: "/", id: "home" },
  { label: "Destinations", to: "/destinations", id: "destinations" },
  { label: "Stays", to: "/stays", id: "stays" },
  { label: "Travel Tools", to: "/#tools", id: "travel-tools" },
  { label: "Trip Planning", to: "/#planning", id: "trip-planning" },
  { label: "Journal", to: "/#journal", id: "journal" },
  { label: "About", to: "/about", id: "about" },
  { label: "Planning Kit", to: "/planning-kit", id: "planning-kit" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-ivory/85 backdrop-blur-xl border-slateblue/15 shadow-[0_8px_30px_rgba(16,42,67,0.06)]"
          : "bg-ivory/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Wordmark />
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={`relative text-[11px] font-medium tracking-[0.18em] uppercase transition-colors duration-300 hover:text-gold after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-gold after:transition-all after:duration-500 ${
                pathname === l.to ? "text-gold after:w-full" : "text-navy after:w-0 hover:after:w-full"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-navy p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-ivory/95 backdrop-blur-xl border-t border-slateblue/15"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    onClick={() => setOpen(false)}
                    className="font-serif text-2xl text-navy hover:text-gold transition-colors duration-300"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
